import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Input, Select, Label, Modal, Empty, Badge } from "../components/UI";
import { SlidePanel } from "../components/SlidePanel";
import { RoomScheduleView } from "../components/RoomScheduleView";
import { RoomBookingsPanel } from "../components/RoomBookingsPanel";
import Holidays from "./Holidays";
import { ProviderSelect } from "../components/ProviderSelect";
import { ExportCsvBtn } from "../components/ExportCsvBtn";
import { ChevronLeft, ChevronRight, Plus, CalendarClock, BellRing, Send, Trash2, ListChecks, CheckCircle2, UserX, Search, Repeat, Flag, Building2 } from "lucide-react";
import { toast } from "sonner";

const APPT_TYPES = ["Individual Therapy", "Intake Assessment", "Medication Review", "Group Therapy", "Telehealth Session"];
const HOURS = Array.from({ length: 10 }, (_, i) => 8 + i);
const EMPTY_FORM = { client_id: "", type: APPT_TYPES[0], date: dayjs().format("YYYY-MM-DD"), start_time: "09:00", end_time: "10:00",
  provider: "", platform: "Zoom for Healthcare", video_link: "", referral_source: "", program: "", prescriber: "", pharmacy: "",
  group_name: "", group_clients: [], location: "", facility_id: "", room_id: "", bed: "" };

function HoverCard({ info }) {
  if (!info) return null;
  const left = Math.min(info.x + 12, window.innerWidth - 260);
  const top = Math.min(info.y + 14, window.innerHeight - 200);
  return (
    <div data-testid="event-hover-popover" style={{ position: "fixed", left, top, zIndex: 95 }}
      className="pointer-events-none w-60 rounded-xl border border-[#EFEBE0] bg-white p-3 text-left shadow-2xl">
      {info.rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2 py-0.5 text-[11px]">
          <span className="text-[#84675B]">{k}</span><span className="font-semibold text-[#3E2723] truncate capitalize">{v || "-"}</span>
        </div>
      ))}
      <div className="mt-1 text-[10px] text-[#84675B]">{info.hint}</div>
    </div>
  );
}

export default function Scheduler() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(dayjs());
  const [appts, setAppts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [moving, setMoving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailForm, setDetailForm] = useState({});
  const [resched, setResched] = useState(null);
  const [acting, setActing] = useState(false);
  const [apptTypes, setApptTypes] = useState(APPT_TYPES);
  useEffect(() => { api.get("/custom-lists").then((r) => r.data.appointment_types?.length && setApptTypes(r.data.appointment_types)).catch(() => {}); }, []);
  const [calTasks, setCalTasks] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [drawerConflicts, setDrawerConflicts] = useState([]);
  const [schedSearch, setSchedSearch] = useState("");
  const [bookingsOpen, setBookingsOpen] = useState(false);
  const [occurrences, setOccurrences] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/facilities").then((r) => setFacilities(r.data)).catch(() => {});
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  const isAdmin = !!me && (me.role === "admin" || me.is_super_admin);

  const loadOccurrences = () => {
    api.get("/room-bookings/occurrences", { params: { start: range[0].format("YYYY-MM-DD"), end: range[1].format("YYYY-MM-DD") } })
      .then((r) => setOccurrences(r.data)).catch(() => setOccurrences([]));
  };
  const loadHolidays = () => {
    api.get("/holidays/range", { params: { start: range[0].format("YYYY-MM-DD"), end: range[1].format("YYYY-MM-DD") } })
      .then((r) => setHolidays(r.data || [])).catch(() => setHolidays([]));
  };
  const holidayByDate = useMemo(() => {
    const m = {};
    (holidays || []).forEach((h) => {
      const arr = m[h.observed_date] || [];
      arr.push(h);
      m[h.observed_date] = arr;
    });
    return m;
  }, [holidays]);

  const range = useMemo(() => {
    if (view === "day" || view === "rooms") return [anchor, anchor];
    if (view === "week") return [anchor.startOf("week"), anchor.endOf("week")];
    return [anchor.startOf("month").startOf("week"), anchor.endOf("month").endOf("week")];
  }, [view, anchor]);

  const load = () => {
    // Single batched call (audit item 91) so the Scheduler month view no longer fires
    // five separate requests. Falls back to the individual endpoints on error so a
    // partial backend rollout never breaks the view.
    const params = { start: range[0].format("YYYY-MM-DD"), end: range[1].format("YYYY-MM-DD") };
    api.get("/scheduler/batch", { params })
      .then((r) => {
        const d = r.data || {};
        setAppts(d.appointments || []);
        setGroups(d.groups || []);
        setCalTasks(d.tasks || []);
        setOccurrences(d.occurrences || []);
        setHolidays(d.holidays || []);
      })
      .catch(() => {
        // Fallback: legacy per-resource loads
        api.get("/appointments", { params }).then((r) => setAppts(r.data));
        api.get("/groups", { params }).then((r) => setGroups(r.data)).catch(() => {});
        api.get("/tasks", { params: { view: "calendar", due_from: params.start, due_to: params.end } }).then((r) => setCalTasks(r.data)).catch(() => {});
        loadOccurrences();
        loadHolidays();
      });
  };
  useEffect(() => { load(); }, [range]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  // Deep-link prefill: /scheduler?client_id=&date=&type=&note=
  const location = useLocation();
  useEffect(() => {
    if (!location.search) return;
    const p = new URLSearchParams(location.search);
    const clientId = p.get("client_id");
    if (!clientId) return;
    const d = p.get("date") || dayjs().format("YYYY-MM-DD");
    const type = p.get("type") || APPT_TYPES[0];
    const note = p.get("note") || "";
    setAnchor(dayjs(d));
    setForm((s) => ({ ...EMPTY_FORM, ...s, client_id: clientId, date: d,
      type: apptTypes.includes(type) ? type : APPT_TYPES[0],
      start_time: "09:00", end_time: "10:00",
      referral_source: note || s.referral_source }));
    setOpen(true);
  }, [location.search, apptTypes]); // eslint-disable-line

  const byDate = useMemo(() => {
    const s = schedSearch.trim().toLowerCase();
    const match = (txt) => !s || String(txt || "").toLowerCase().includes(s);
    const m = {};
    appts.filter((a) => match(`${a.client_name} ${a.type} ${a.room_label || ""}`)).forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    groups.filter((g) => match(g.name)).forEach((g) => { (m[g.date] = m[g.date] || []).push({ ...g, _group: true, start_time: g.start_time, client_name: g.name, type: "Group" }); });
    calTasks.filter((t) => match(t.title)).forEach((t) => { (m[t.due_date] = m[t.due_date] || []).push({ ...t, _task: true, start_time: "", client_name: t.title, type: "Task" }); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.start_time || "zz").localeCompare(b.start_time || "zz")));
    return m;
  }, [appts, groups, calTasks, schedSearch]);

  const move = (dir) => setAnchor(anchor.add(dir, view === "day" || view === "rooms" ? "day" : view === "week" ? "week" : "month"));

  const openNew = (date, startHour, room) => {
    setForm({ ...EMPTY_FORM, date: date || dayjs().format("YYYY-MM-DD"),
      start_time: startHour != null ? `${String(startHour).padStart(2, "0")}:00` : "09:00",
      end_time: startHour != null ? `${String(startHour + 1).padStart(2, "0")}:00` : "10:00",
      facility_id: room ? room.facility_id : "", room_id: room ? room.id : "" });
    setOpen(true);
  };

  useEffect(() => {
    if (!open || !form.room_id || !form.date || !form.start_time || !form.end_time) { setConflicts([]); return; }
    const t = setTimeout(() => {
      api.get(`/rooms/${form.room_id}/conflicts`, { params: { date: form.date, start: form.start_time, end: form.end_time, bed: form.bed || "" } })
        .then((r) => setConflicts(r.data)).catch(() => setConflicts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, form.room_id, form.date, form.start_time, form.end_time, form.bed]);

  useEffect(() => {
    if (!detail || !detailForm.room_id || !detailForm.date || !detailForm.start_time || !detailForm.end_time) { setDrawerConflicts([]); return; }
    const t = setTimeout(() => {
      api.get(`/rooms/${detailForm.room_id}/conflicts`, { params: { date: detailForm.date, start: detailForm.start_time, end: detailForm.end_time, bed: detailForm.bed || "", exclude: detail.id } })
        .then((r) => setDrawerConflicts(r.data)).catch(() => setDrawerConflicts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [detail, detailForm.room_id, detailForm.date, detailForm.start_time, detailForm.end_time, detailForm.bed]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.type === "Group Therapy") {
        if (!form.group_name.trim()) return toast.error("Enter a group name");
        if (form.group_clients.length === 0) return toast.error("Select at least one client for the group");
        await api.post("/groups", { name: form.group_name, date: form.date, start_time: form.start_time, end_time: form.end_time,
          group_type: "Process Group", facilitator: form.provider, location: (rooms.find((r) => r.id === form.room_id) || {}).name || form.location, status: "scheduled",
          clients: form.group_clients.map((id) => { const c = clients.find((x) => x.id === id); return { id, name: c ? `${c.first_name} ${c.last_name}` : "" }; }) });
        toast.success("Group session scheduled");
      } else {
        const extras = [form.provider && `Provider: ${form.provider}`, form.platform && form.type === "Telehealth Session" && `Platform: ${form.platform}`,
          form.video_link && `Link: ${form.video_link}`, form.referral_source && `Referral source: ${form.referral_source}`,
          form.program && `Program: ${form.program}`, form.prescriber && `Prescriber: ${form.prescriber}`,
          form.pharmacy && `Pharmacy: ${form.pharmacy}`, form.location && `Location: ${form.location}`].filter(Boolean).join(" · ");
        await api.post("/appointments", { client_id: form.client_id, type: form.type, date: form.date,
          start_time: form.start_time, end_time: form.end_time, notes: extras,
          facility_id: form.facility_id, room_id: form.room_id, bed: form.bed });
        toast.success("Appointment booked");
      }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const onDrop = (dateStr) => {
    setDropTarget(null);
    const a = appts.find((x) => x.id === dragId);
    setDragId(null);
    if (!a || a.date === dateStr) return;
    setPendingMove({ appt: a, newDate: dateStr });
  };

  const confirmMove = async () => {
    setMoving(true);
    try {
      await api.put(`/appointments/${pendingMove.appt.id}`, { date: pendingMove.newDate });
      toast.success(`Moved to ${dayjs(pendingMove.newDate).format("MMM D, YYYY")}`);
      setPendingMove(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setMoving(false); }
  };

  const doAction = async (path, msg) => {
    setActing(true);
    try {
      await api.post(`/appointments/${detail.id}/${path}`, {});
      toast.success(msg); setDetail(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setActing(false); }
  };

  const dropProps = (dateStr) => ({
    onDragOver: (e) => { if (dragId) { e.preventDefault(); setDropTarget(dateStr); } },
    onDragLeave: () => setDropTarget((t) => (t === dateStr ? null : t)),
    onDrop: (e) => { e.preventDefault(); onDrop(dateStr); },
  });

  const [hover, setHover] = useState(null);
  const hoverProps = (rows, hint) => ({
    onMouseEnter: (e) => setHover({ rows, hint, x: e.clientX, y: e.clientY }),
    onMouseLeave: () => setHover(null),
  });

  const renderChip = (a) => a._task ? (
    <div key={a.id} data-testid={`caltask-chip-${a.id}`} onClick={(e) => { e.stopPropagation(); window.location.href = "/tasks"; }}
      {...hoverProps([["Task", a.title], ["Assigned", a.assigned_to_name || "Unassigned"], ["Priority", a.priority], ["Status", a.status], ["Due", a.due_date]], "Click to open the task board")}
      className="rounded-lg bg-[#5E35B1] text-white px-2 py-1 text-[11px] leading-tight mb-1 truncate cursor-pointer">
      <ListChecks size={10} className="inline mr-1 -mt-0.5" />{a.title}
    </div>
  ) : a._group ? (
    <div key={a.id} data-testid={`group-chip-${a.id}`} onClick={(e) => { e.stopPropagation(); window.location.href = "/groups"; }}
      {...hoverProps([["Group", a.name], ["Facilitator", a.facilitator || "-"], ["Time", `${a.start_time || ""}${a.end_time ? " to " + a.end_time : ""}`], ["Date", a.date]], "Click to open Groups")}
      className="rounded-lg bg-[#FFB300] text-[#3E2723] px-2 py-1 text-[11px] leading-tight mb-1 truncate cursor-pointer">
      <span className="font-semibold">{a.start_time}</span> ⬢ {a.name}
    </div>
  ) : (
    <div key={a.id} data-testid={`appt-chip-${a.id}`} draggable
      onClick={(e) => { e.stopPropagation(); setHover(null); setResched(null); setDetail(a); setDetailForm({ type: a.type, date: a.date, start_time: a.start_time, end_time: a.end_time, status: a.status || "scheduled", facility_id: a.facility_id || "", room_id: a.room_id || "", bed: a.bed || "" }); }}
      onDragStart={(e) => { e.stopPropagation(); setHover(null); setDragId(a.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => { setDragId(null); setDropTarget(null); }}
      {...hoverProps([["Client", a.client_name], ["Type", a.type], ["Time", `${a.start_time} to ${a.end_time || ""}`], ["Date", dayjs(a.date).format("MMM D, YYYY")], ["Status", a.status || "scheduled"], ["Room", a.room_label || "-"]], "Click for details · drag to reschedule")}
      className={`rounded-lg text-white px-2 py-1 text-[11px] leading-tight mb-1 truncate cursor-grab active:cursor-grabbing ${
        a.status === "missed" || a.status === "no-show" ? "bg-[#C62828]" : a.status === "checked_in" ? "bg-[#2E7D32]"
        : a.status === "rescheduled" || a.status === "cancelled" ? "bg-[#9E9E9E] line-through opacity-70" : a.status === "completed" ? "bg-[#00695C]" : "bg-[#4E342E]"
      } ${dragId === a.id ? "opacity-40 ring-2 ring-[#FFB300]" : ""}`}>
      <span className="font-semibold">{a.start_time}</span> {a.client_name}
    </div>
  );

  const days = useMemo(() => {
    const out = [];
    let d = range[0];
    while (d.isBefore(range[1]) || d.isSame(range[1], "day")) { out.push(d); d = d.add(1, "day"); }
    return out;
  }, [range]);

  const cellHighlight = (dateStr) => dropTarget === dateStr ? "ring-2 ring-[#FFB300] bg-[#FFF8E1]" : "";

  // Google-Calendar-style day popover: clicking "+N more" opens a floating card
  // that lists every event on that day (appointments, groups, tasks, holidays)
  // so users no longer lose sight of overflow items.
  const [dayPopover, setDayPopover] = useState(null);
  const openDayPopover = (e, dateStr) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDayPopover({
      date: dateStr,
      x: Math.min(rect.left, window.innerWidth - 340),
      y: Math.min(rect.bottom + 6, window.innerHeight - 380),
    });
  };
  useEffect(() => {
    if (!dayPopover) return;
    const h = (e) => {
      if (!e.target.closest?.("[data-day-popover]")) setDayPopover(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [dayPopover]);

  return (
    <div>
      <PageHeader title="Scheduler" subtitle="Click a day to book · drag appointments to reschedule · yellow tint marks US federal holidays"
        action={
          <div className="flex items-center gap-2">
            <ExportCsvBtn resource="appointments" />
            <Btn variant="outline" data-testid="recurring-blocks-btn" onClick={() => setBookingsOpen(true)}>
              <Repeat size={16} /> Recurring Blocks
            </Btn>
            <Btn data-testid="new-appointment-btn" onClick={() => openNew()}><Plus size={16} /> New Appointment</Btn>
          </div>
        } />
      <Card className="p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="scheduler-prev-btn" aria-label="Previous" onClick={() => move(-1)} className="p-2 rounded-full hover:bg-[#F5F2EB]"><ChevronLeft size={18} /></button>
          <span className="font-heading font-bold text-[#3E2723] min-w-[180px] text-center" data-testid="scheduler-period-label">
            {view === "month" ? anchor.format("MMMM YYYY") : view === "week" ? `Week of ${anchor.startOf("week").format("MMM D, YYYY")}` : anchor.format("dddd, MMM D, YYYY")}
          </span>
          <button data-testid="scheduler-next-btn" aria-label="Next" onClick={() => move(1)} className="p-2 rounded-full hover:bg-[#F5F2EB]"><ChevronRight size={18} /></button>
          <Btn variant="outline" data-testid="scheduler-today-btn" className="px-3 py-1 text-xs" onClick={() => setAnchor(dayjs())}>Today</Btn>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
            <Input data-testid="scheduler-search" className="h-9 w-52 pl-9" placeholder="Search client, type, room…" value={schedSearch} onChange={(e) => setSchedSearch(e.target.value)} />
          </div>
          <div className="flex gap-1 rounded-full bg-[#F5F2EB] p-1">
            {["day", "week", "month", "rooms", "holidays"].map((v) => (
              <button key={v} data-testid={`scheduler-view-${v}`} onClick={() => setView(v)}
                className={`rounded-full px-4 py-1 text-xs font-semibold capitalize transition-colors ${view === v ? "bg-[#4E342E] text-white" : "text-[#6D4C41]"}`}>{v}</button>
            ))}
          </div>
        </div>
      </Card>

      {view === "day" && (
        <Card className="p-5" data-testid="scheduler-day-view">
          {(holidayByDate[anchor.format("YYYY-MM-DD")] || []).map((h, i) => (
            <div key={`${h.observed_date}-${i}`} data-testid={i === 0 ? "day-holiday-banner" : `day-holiday-banner-${i}`}
              style={{ borderColor: h.source === "clinic" ? (h.color || "#7E57C2") : "#FFB300" }}
              className="mb-3 flex items-start gap-2 rounded-xl border bg-[#FFF8E1] p-3 text-xs text-[#8D6E00]">
              {h.source === "clinic" ? <Building2 size={14} className="mt-0.5 shrink-0 text-[#7E57C2]" /> : <Flag size={14} className="mt-0.5 shrink-0 text-[#8D5A00]" />}
              <div>
                <div className="font-bold text-[#3E2723]">{h.name}{h.source === "clinic" ? " (Clinic Holiday)" : ""}</div>
                <div className="mt-0.5 text-[11px] text-[#6D4C41]">{h.description}</div>
              </div>
            </div>
          ))}
          {HOURS.map((h) => {
            const slotAppts = (byDate[anchor.format("YYYY-MM-DD")] || []).filter((a) => parseInt(a.start_time) === h);
            return (
              <div key={h} data-testid={`scheduler-hour-${h}`} onClick={() => openNew(anchor.format("YYYY-MM-DD"), h)}
                className="flex border-b border-[#EFEBE0] last:border-0 py-2 cursor-pointer hover:bg-[#FDFCF8] rounded-lg px-1 transition-colors">
                <div className="w-16 shrink-0 text-xs text-[#84675B] pt-1">{h}:00</div>
                <div className="flex-1">{slotAppts.map((a) => renderChip(a))}</div>
              </div>
            );
          })}
        </Card>
      )}

      {view === "week" && (
        <Card className="p-4 overflow-x-auto" data-testid="scheduler-week-view">
          <div className="grid grid-cols-7 gap-2 min-w-[840px]">
            {days.map((d) => {
              const ds = d.format("YYYY-MM-DD");
              const hols = holidayByDate[ds] || [];
              const primary = hols[0];
              return (
                <div key={ds} data-testid={`scheduler-day-cell-${ds}`} onClick={() => openNew(ds)} {...dropProps(ds)}
                  className={`rounded-xl p-2 min-h-[240px] cursor-pointer transition-colors hover:bg-[#F5F2EB]/70 ${d.isSame(dayjs(), "day") ? "bg-[#F5F2EB] ring-1 ring-[#8D6E63]" : primary ? "bg-[#FFF8E1]" : "bg-[#FDFCF8]"} ${cellHighlight(ds)}`}>
                  <div className="mb-2 text-center pointer-events-none">
                    <div className="text-[10px] uppercase font-bold text-[#7B6157]">{d.format("ddd")}</div>
                    <div className="font-heading font-bold text-[#3E2723]">{d.format("D")}</div>
                    {primary && (
                      <div data-testid={`hol-chip-${ds}`}
                        style={{ background: primary.source === "clinic" ? (primary.color || "#7E57C2") : "#FFB300", color: primary.source === "clinic" ? "#FFFFFF" : "#3E2723" }}
                        className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-px text-[9px] font-bold">
                        {primary.source === "clinic" ? <Building2 size={8} /> : <Flag size={8} />}
                        {primary.name.split(" ").slice(0, 2).join(" ")}{hols.length > 1 ? ` +${hols.length - 1}` : ""}
                      </div>
                    )}
                  </div>
                  {(byDate[ds] || []).map((a) => renderChip(a))}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "month" && (
        <Card className="p-4 overflow-x-auto" data-testid="scheduler-month-view">
          <div className="grid grid-cols-7 gap-1.5 min-w-[840px]">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-center text-[10px] uppercase font-bold text-[#7B6157] py-1">{d}</div>
            ))}
            {days.map((d) => {
              const ds = d.format("YYYY-MM-DD");
              const hols = holidayByDate[ds] || [];
              const primary = hols[0];
              return (
                <div key={ds} data-testid={`scheduler-day-cell-${ds}`} onClick={() => openNew(ds)} {...dropProps(ds)}
                  className={`rounded-xl p-1.5 min-h-[92px] border cursor-pointer transition-colors hover:bg-[#F5F2EB]/70 ${d.isSame(anchor, "month") ? "border-[#EFEBE0] bg-white" : "border-transparent bg-[#FDFCF8] opacity-50"} ${d.isSame(dayjs(), "day") ? "ring-1 ring-[#8D6E63]" : ""} ${primary ? "bg-[#FFF8E1]" : ""} ${cellHighlight(ds)}`}>
                  <div className="flex items-center justify-between gap-1 mb-1 pointer-events-none">
                    <div className="text-xs font-semibold text-[#6D4C41]">{d.format("D")}</div>
                    {primary && (
                      <span data-testid={`hol-chip-${ds}`} title={hols.map((h) => `${h.name}${h.source === "clinic" ? " (clinic)" : ""}${h.observed_date !== h.date ? " (observed)" : ""}`).join(" | ")}
                        style={{ background: primary.source === "clinic" ? (primary.color || "#7E57C2") : "#FFB300", color: primary.source === "clinic" ? "#FFFFFF" : "#3E2723" }}
                        className="inline-flex max-w-[75%] items-center gap-1 truncate rounded-full px-1.5 py-px text-[9px] font-bold">
                        {primary.source === "clinic" ? <Building2 size={8} className="shrink-0" /> : <Flag size={8} className="shrink-0" />}
                        {primary.name.split(" ")[0]}{hols.length > 1 ? ` +${hols.length - 1}` : ""}
                      </span>
                    )}
                  </div>
                  {(byDate[ds] || []).slice(0, 2).map((a) => renderChip(a))}
                  {(byDate[ds] || []).length > 2 && (
                    <button data-testid={`day-more-btn-${ds}`} onClick={(e) => openDayPopover(e, ds)}
                      className="text-[10px] font-semibold text-[#5D4037] hover:text-[#3E2723] hover:underline cursor-pointer bg-transparent border-0 p-0">
                      +{byDate[ds].length - 2} more
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "rooms" && (
        <RoomScheduleView facilities={facilities} rooms={rooms}
          appts={(byDate[anchor.format("YYYY-MM-DD")] || []).filter((a) => !a._group && !a._task)}
          occurrences={occurrences.filter((o) => o.date === anchor.format("YYYY-MM-DD"))}
          onSlot={(room, h) => openNew(anchor.format("YYYY-MM-DD"), h, room)}
          onAppt={(a) => { setResched(null); setDetail(a); setDetailForm({ type: a.type, date: a.date, start_time: a.start_time, end_time: a.end_time, status: a.status || "scheduled", facility_id: a.facility_id || "", room_id: a.room_id || "", bed: a.bed || "" }); }} />
      )}

      {view === "holidays" && (
        <Card className="p-4" data-testid="scheduler-holidays-view">
          <Holidays embedded />
        </Card>
      )}

      <RoomBookingsPanel open={bookingsOpen} onClose={() => setBookingsOpen(false)}
        facilities={facilities} rooms={rooms} isAdmin={isAdmin} onChange={loadOccurrences} />

      <Modal open={open} onClose={() => setOpen(false)} title="New Appointment">
        <form onSubmit={save} className="space-y-4">
          <div><Label>Type</Label>
            <Select data-testid="appt-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {apptTypes.map((t) => <option key={t}>{t}</option>)}
            </Select>
          </div>
          {form.type !== "Group Therapy" ? (
            <div><Label>Client</Label>
              <Select data-testid="appt-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} required>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} (HRN {c.health_record_number || c.mrn})</option>)}
              </Select>
            </div>
          ) : (
            <>
              <div><Label>Group name</Label><Input data-testid="appt-group-name" value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })} placeholder="e.g., DBT Skills Group" required /></div>
              <div><Label>Clients ({form.group_clients.length} selected)</Label>
                <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-[#E5DFD3] p-2 grid grid-cols-2 gap-1" data-testid="appt-group-roster">
                  {clients.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-[#F5F2EB] cursor-pointer">
                      <input type="checkbox" data-testid={`appt-group-client-${c.id}`} className="h-4 w-4 accent-[#4E342E]"
                        checked={form.group_clients.includes(c.id)}
                        onChange={() => setForm((s) => ({ ...s, group_clients: s.group_clients.includes(c.id) ? s.group_clients.filter((x) => x !== c.id) : [...s.group_clients, c.id] }))} />
                      <span className="truncate">{c.first_name} {c.last_name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div><Label>Facilitator</Label><Input data-testid="appt-group-facilitator" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
            </>
          )}
          {form.type === "Telehealth Session" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Platform</Label>
                <Select data-testid="appt-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                  {["Zoom for Healthcare", "Doxy.me", "Google Meet (BAA)", "Teams (BAA)"].map((p) => <option key={p}>{p}</option>)}
                </Select></div>
              <div><Label>Video link</Label><Input data-testid="appt-video-link" value={form.video_link} onChange={(e) => setForm({ ...form, video_link: e.target.value })} placeholder="https://…" /></div>
            </div>
          )}
          {form.type === "Intake Assessment" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Referral source</Label><Input data-testid="appt-referral-source" value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} /></div>
              <div><Label>Program</Label><Input data-testid="appt-program" value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} /></div>
            </div>
          )}
          {form.type === "Medication Review" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prescriber</Label>
                <ProviderSelect testId="appt-prescriber" value={form.prescriber}
                  onChange={(v) => setForm({ ...form, prescriber: v })}
                  placeholder="Search prescribers by name, org, or NPI" />
              </div>
              <div><Label>Pharmacy</Label><Input data-testid="appt-pharmacy" value={form.pharmacy} onChange={(e) => setForm({ ...form, pharmacy: e.target.value })} /></div>
            </div>
          )}
          {form.type === "Individual Therapy" && (
            <div><Label>Provider / clinician</Label><Input data-testid="appt-provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
          )}
          <div><Label>Date</Label><Input data-testid="appt-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start</Label><Input data-testid="appt-start-input" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required /></div>
            <div><Label>End</Label><Input data-testid="appt-end-input" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required /></div>
          </div>
          <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 space-y-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#7B6157]">Location (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Facility</Label>
                <Select data-testid="appt-facility-select" value={form.facility_id} onChange={(e) => setForm({ ...form, facility_id: e.target.value, room_id: "", bed: "" })}>
                  <option value="">No facility</option>
                  {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Select></div>
              <div><Label>Room</Label>
                <Select data-testid="appt-room-select" value={form.room_id} onChange={(e) => { const rm = rooms.find((r) => r.id === e.target.value); setForm({ ...form, room_id: e.target.value, facility_id: rm ? rm.facility_id : form.facility_id, bed: "" }); }}>
                  <option value="">No room</option>
                  {rooms.filter((r) => !form.facility_id || r.facility_id === form.facility_id).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.unit ? ` (${r.unit})` : ""}</option>
                  ))}
                </Select></div>
            </div>
            {(() => { const rm = rooms.find((r) => r.id === form.room_id); return rm && (rm.beds || []).length > 0 ? (
              <div><Label>Bed</Label>
                <Select data-testid="appt-bed-select" value={form.bed} onChange={(e) => setForm({ ...form, bed: e.target.value })}>
                  <option value="">No bed</option>
                  {rm.beds.map((b) => <option key={b}>{b}</option>)}
                </Select></div>
            ) : null; })()}
            {conflicts.length > 0 && (
              <div data-testid="appt-conflict-warning" className="rounded-xl border border-[#FFB300] bg-[#FFF8E1] p-3 text-xs text-[#8D6E00]">
                <div className="font-bold mb-1">Room conflict warning</div>
                {conflicts.map((c) => (
                  <div key={c.id}>
                    {c.start_time}-{c.end_time} · {c.client_name} ({c.type}){c.bed ? ` · Bed ${c.bed}` : ""}
                    {c.kind === "recurring_block" && <span className="ml-1 rounded bg-[#F5F2EB] px-1 py-px text-[9px] font-bold text-[#4E342E]">Recurring block</span>}
                  </div>
                ))}
                <div className="mt-1 text-[11px]">You can still book this room, this is a warning only.</div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="appt-save-btn" type="submit">Book</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!pendingMove} onClose={() => setPendingMove(null)} title="Reschedule appointment?">
        {pendingMove && (
          <div className="space-y-4" data-testid="move-confirm-dialog">
            <div className="flex items-start gap-3 rounded-xl bg-[#F5F2EB] p-4">
              <CalendarClock size={20} className="mt-0.5 shrink-0 text-[#7B6157]" />
              <div className="text-sm text-[#3E2723]">
                Move <span className="font-bold">{pendingMove.appt.client_name}</span> - {pendingMove.appt.type}
                <div className="mt-1 text-[#5D4037]">
                  from <span className="font-semibold">{dayjs(pendingMove.appt.date).format("ddd, MMM D")}</span> at {pendingMove.appt.start_time}
                  {" "}to <span className="font-semibold text-[#4E342E]">{dayjs(pendingMove.newDate).format("ddd, MMM D, YYYY")}</span>?
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" data-testid="move-cancel-btn" onClick={() => setPendingMove(null)}>Cancel</Btn>
              <Btn data-testid="move-confirm-btn" onClick={confirmMove} disabled={moving}>{moving ? "Moving…" : "Confirm Move"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <SlidePanel open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.client_name} - ${detail.type}` : ""} subtitle={detail ? dayjs(detail.date).format("dddd, MMM D, YYYY") : ""} testId="appt-detail-drawer">
        {detail && (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#F5F2EB] p-4 text-sm text-[#3E2723]" data-testid="appt-detail-info">
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Client</span><span className="font-semibold">{detail.client_name}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">HRN</span><span className="font-semibold tabular-nums" data-testid="drawer-client-hrn">{(clients.find((c) => c.id === detail.client_id) || {}).health_record_number || "-"}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Room</span><span className="font-semibold" data-testid="drawer-room-label">{detail.room_label || "Not assigned"}{detail.bed ? ` · Bed ${detail.bed}` : ""}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Status</span><Badge value={detailForm.status} /></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Booked</span><span>{(detail.created_at || "").slice(0, 10) || "-"}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="drawer-checkin-btn" disabled={acting || ["checked_in", "completed", "rescheduled"].includes(detailForm.status)}
                onClick={() => doAction("check-in", `${detail.client_name} checked in`)}
                className="flex flex-col items-center gap-1 rounded-xl border border-[#C8E6C9] bg-[#E8F5E9] px-2 py-2.5 text-xs font-bold text-[#2E7D32] hover:bg-[#C8E6C9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <CheckCircle2 size={16} /> Check In
              </button>
              <button data-testid="drawer-missed-btn" disabled={acting || ["missed", "completed", "rescheduled"].includes(detailForm.status)}
                onClick={() => doAction("missed", "Marked as missed - client & staff alerted")}
                className="flex flex-col items-center gap-1 rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] px-2 py-2.5 text-xs font-bold text-[#C62828] hover:bg-[#FFCDD2] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <UserX size={16} /> Missed
              </button>
              <button data-testid="drawer-reschedule-btn" disabled={acting || detailForm.status === "rescheduled"}
                onClick={() => setResched(resched ? null : { date: detail.date, start_time: detail.start_time, end_time: detail.end_time })}
                className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${resched ? "border-[#FFB300] bg-[#FFF8E1] text-[#8D6E00]" : "border-[#D7CCC8] bg-[#F5F2EB] text-[#5D4037] hover:bg-[#EDE7DC]"}`}>
                <CalendarClock size={16} /> Reschedule
              </button>
            </div>
            {resched && (
              <div className="rounded-xl border border-[#FFB300] bg-[#FFF8E1] p-3 space-y-3" data-testid="drawer-reschedule-panel">
                <div className="text-xs font-bold text-[#8D6E00]">New date & time - the original appointment stays in the history as “rescheduled”.</div>
                <div><Label>New date</Label><Input data-testid="resched-date" type="date" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start</Label><Input data-testid="resched-start" type="time" value={resched.start_time} onChange={(e) => setResched({ ...resched, start_time: e.target.value })} /></div>
                  <div><Label>End</Label><Input data-testid="resched-end" type="time" value={resched.end_time} onChange={(e) => setResched({ ...resched, end_time: e.target.value })} /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Btn variant="ghost" className="text-xs" onClick={() => setResched(null)}>Cancel</Btn>
                  <Btn data-testid="resched-confirm-btn" className="text-xs" disabled={acting} onClick={async () => {
                    setActing(true);
                    try {
                      await api.post(`/appointments/${detail.id}/reschedule`, resched);
                      toast.success(`Rescheduled to ${dayjs(resched.date).format("MMM D, YYYY")} at ${resched.start_time} - client & staff alerted`);
                      setResched(null); setDetail(null); load();
                    } catch (err) { toast.error(apiError(err)); } finally { setActing(false); }
                  }}>{acting ? "Rescheduling…" : "Confirm Reschedule"}</Btn>
                </div>
              </div>
            )}
            <div><Label>Type</Label>
              <Select data-testid="drawer-type" value={detailForm.type} onChange={(e) => setDetailForm({ ...detailForm, type: e.target.value })}>
                {[...new Set([...apptTypes, detailForm.type].filter(Boolean))].map((t) => <option key={t}>{t}</option>)}
              </Select></div>
            <div><Label>Date</Label><Input data-testid="drawer-date" type="date" value={detailForm.date} onChange={(e) => setDetailForm({ ...detailForm, date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input data-testid="drawer-start" type="time" value={detailForm.start_time} onChange={(e) => setDetailForm({ ...detailForm, start_time: e.target.value })} /></div>
              <div><Label>End</Label><Input data-testid="drawer-end" type="time" value={detailForm.end_time} onChange={(e) => setDetailForm({ ...detailForm, end_time: e.target.value })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select data-testid="drawer-status" value={detailForm.status} onChange={(e) => setDetailForm({ ...detailForm, status: e.target.value })}>
                {["scheduled", "checked_in", "completed", "missed", "no-show", "cancelled", "rescheduled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Facility</Label>
                <Select data-testid="drawer-facility" value={detailForm.facility_id || ""} onChange={(e) => setDetailForm({ ...detailForm, facility_id: e.target.value, room_id: "", bed: "" })}>
                  <option value="">No facility</option>
                  {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Select></div>
              <div><Label>Room</Label>
                <Select data-testid="drawer-room" value={detailForm.room_id || ""} onChange={(e) => { const rm = rooms.find((r) => r.id === e.target.value); setDetailForm({ ...detailForm, room_id: e.target.value, facility_id: rm ? rm.facility_id : detailForm.facility_id, bed: "" }); }}>
                  <option value="">No room</option>
                  {rooms.filter((r) => !detailForm.facility_id || r.facility_id === detailForm.facility_id).map((r) => <option key={r.id} value={r.id}>{r.name}{r.unit ? ` (${r.unit})` : ""}</option>)}
                </Select></div>
            </div>
            {(() => { const rm = rooms.find((r) => r.id === detailForm.room_id); return rm && (rm.beds || []).length > 0 ? (
              <div><Label>Bed</Label>
                <Select data-testid="drawer-bed" value={detailForm.bed || ""} onChange={(e) => setDetailForm({ ...detailForm, bed: e.target.value })}>
                  <option value="">No bed</option>
                  {rm.beds.map((b) => <option key={b}>{b}</option>)}
                </Select></div>
            ) : null; })()}
            {drawerConflicts.length > 0 && (
              <div data-testid="drawer-conflict-warning" className="rounded-xl border border-[#FFB300] bg-[#FFF8E1] p-3 text-xs text-[#8D6E00]">
                <div className="font-bold mb-1">Room conflict warning</div>
                {drawerConflicts.map((c) => (
                  <div key={c.id}>
                    {c.start_time}-{c.end_time} · {c.client_name} ({c.type})
                    {c.kind === "recurring_block" && <span className="ml-1 rounded bg-[#F5F2EB] px-1 py-px text-[9px] font-bold text-[#4E342E]">Recurring block</span>}
                  </div>
                ))}
                <div className="mt-1 text-[11px]">Saving is still allowed, this is a warning only.</div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Btn data-testid="drawer-save-btn" onClick={async () => {
                try { await api.put(`/appointments/${detail.id}`, detailForm); toast.success("Appointment updated"); setDetail(null); load(); }
                catch (err) { toast.error(apiError(err)); }
              }}>Save Changes</Btn>
              <Btn variant="outline" data-testid="drawer-remind-btn" disabled={acting} onClick={async () => {
                setActing(true);
                try { await api.post(`/appointments/${detail.id}/remind`, {}); toast.success("Reminder sent - staff alerted & client notified"); }
                catch (err) { toast.error(apiError(err)); } finally { setActing(false); }
              }}><BellRing size={14} /> Send Reminder</Btn>
              <Btn variant="outline" data-testid="drawer-notify-client-btn" onClick={async () => {
                try {
                  await api.post("/client-notifications", { client_id: detail.client_id, subject: "Appointment reminder", type: "appointment",
                    message: `Reminder: your ${detail.type} appointment is on ${dayjs(detailForm.date).format("MMMM D, YYYY")} at ${detailForm.start_time}.` });
                  toast.success(`Notification sent to ${detail.client_name}`);
                } catch (err) { toast.error(apiError(err)); }
              }}><Send size={14} /> Notify Client</Btn>
              <Btn variant="ghost" data-testid="drawer-delete-btn" className="text-[#C62828]" onClick={async () => {
                try { await api.delete(`/appointments/${detail.id}`); toast.success("Appointment deleted"); setDetail(null); load(); }
                catch (err) { toast.error(apiError(err)); }
              }}><Trash2 size={14} /> Delete</Btn>
            </div>
          </div>
        )}
      </SlidePanel>

      <HoverCard info={hover} />

      {dayPopover && (
        <div data-day-popover data-testid={`day-popover-${dayPopover.date}`}
          style={{ position: "fixed", left: dayPopover.x, top: dayPopover.y, zIndex: 100 }}
          className="w-80 rounded-2xl border border-[#EFEBE0] bg-white p-4 shadow-[0_18px_44px_-12px_rgba(62,39,35,0.45)]">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase font-bold text-[#7B6157] tracking-wider">{dayjs(dayPopover.date).format("dddd")}</div>
              <div className="font-heading text-xl font-bold text-[#3E2723]">{dayjs(dayPopover.date).format("MMM D, YYYY")}</div>
            </div>
            <button data-testid="day-popover-close" onClick={() => setDayPopover(null)}
              className="h-8 w-8 rounded-full text-[#7B6157] hover:bg-[#F5F2EB] flex items-center justify-center">×</button>
          </div>
          {(holidayByDate[dayPopover.date] || []).map((h, i) => (
            <div key={`h-${i}`} className="mb-2 flex items-start gap-2 rounded-lg p-2"
              style={{ background: h.source === "clinic" ? (h.color || "#7E57C2") + "22" : "#FFF8E1" }}>
              {h.source === "clinic" ? <Building2 size={13} className="mt-0.5 shrink-0" /> : <Flag size={13} className="mt-0.5 shrink-0 text-[#8D5A00]" />}
              <div className="text-xs">
                <div className="font-bold text-[#3E2723]">{h.name}</div>
                {h.description && <div className="text-[#6D4C41] mt-0.5">{h.description}</div>}
              </div>
            </div>
          ))}
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {(byDate[dayPopover.date] || []).length === 0 && (holidayByDate[dayPopover.date] || []).length === 0 && (
              <div className="py-6 text-center text-xs text-[#84675B]">Nothing scheduled on this day.</div>
            )}
            {(byDate[dayPopover.date] || []).map((a) => (
              <div key={a.id} onClick={() => { setDayPopover(null); if (a._task) { window.location.href = "/tasks"; } else if (a._group) { window.location.href = "/groups"; } else { setDetail(a); setDetailForm({ type: a.type, date: a.date, start_time: a.start_time, end_time: a.end_time, status: a.status || "scheduled", facility_id: a.facility_id || "", room_id: a.room_id || "", bed: a.bed || "" }); } }}
                data-testid={`day-popover-event-${a.id}`}
                className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-[12px] cursor-pointer transition-colors ${a._task ? "bg-[#EDE7F6] hover:bg-[#D1C4E9]" : a._group ? "bg-[#FFF8E1] hover:bg-[#FFECB3]" : a.status === "cancelled" || a.status === "rescheduled" ? "bg-[#F5F5F5] line-through opacity-70 hover:bg-[#EEE]" : "bg-[#F5F2EB] hover:bg-[#EDE7DC]"}`}>
                <span className="font-mono text-[11px] font-bold text-[#5D4037] w-12 shrink-0">{a.start_time || "-"}</span>
                <span className="font-semibold text-[#3E2723] truncate">{a.client_name}</span>
                {a.type && <span className="text-[10px] text-[#7B6157] ml-auto shrink-0">{a.type}</span>}
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-[#EFEBE0] flex justify-end">
            <button data-testid="day-popover-add-btn" onClick={() => { setDayPopover(null); openNew(dayPopover.date); }}
              className="rounded-full bg-[#4E342E] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#5D4037] inline-flex items-center gap-1">
              <Plus size={12} /> Add on this day
            </button>
          </div>
        </div>
      )}

      {appts.length === 0 && <Empty text="No appointments in this period" />}
    </div>
  );
}
